import { useCallback, useEffect, useState } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import UserShowsPage from '../components/UserShowsPage';
import SeenShowsHeatmap from '../components/SeenShowsHeatmap';
import SeenNoteModal from '../components/SeenNoteModal';
import { Link, useParams } from '../util/router';
import { requireAuth, useAuth } from '../util/auth';
import { getSeenShowRowsForHeatmap, getSeenShowsDataByUserPaginated } from '../util/db';

const PAGE_SIZE = 100;

function SeenShowsPage() {
  const { userId } = useParams();
  const auth = useAuth();
  const isOwner = auth.user?.id === userId;
  const [heatmapRows, setHeatmapRows] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingShow, setEditingShow] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [heatRows, seenShows] = await Promise.all([
        getSeenShowRowsForHeatmap(userId),
        getSeenShowsDataByUserPaginated(userId, PAGE_SIZE, 0),
      ]);
      setHeatmapRows(heatRows);
      setShows(seenShows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSeenToggle = useCallback(
    (showId, result) => {
      if (result?.removed) {
        setShows(prev => prev.filter(s => s.show_id !== showId));
      }
      if (result?.added || result?.removed) {
        getSeenShowRowsForHeatmap(userId).then(setHeatmapRows).catch(console.error);
      }
    },
    [userId]
  );

  const renderNote = show =>
    show.notes || isOwner ? (
      <div className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm">
        {show.notes ? (
          <p className="whitespace-pre-line text-black/70">{show.notes}</p>
        ) : (
          <p className="text-black/40">No note yet</p>
        )}
        {isOwner && (
          <button
            type="button"
            onClick={() => setEditingShow(show)}
            className="mt-1 flex items-center gap-1 text-xs font-semibold text-secondary-red"
          >
            <PencilSquareIcon className="w-4" /> {show.notes ? 'Edit note' : 'Add note'}
          </button>
        )}
      </div>
    ) : null;

  return (
    <>
      <UserShowsPage
        userId={userId}
        heading="Seen Shows"
        shows={shows}
        loading={loading}
        source="seen_shows"
        onSeenToggle={handleSeenToggle}
        renderExtra={renderNote}
        beforeShows={
          !loading && (
            <div className="mb-4 px-4 md:px-8">
              <SeenShowsHeatmap data={heatmapRows} />
            </div>
          )
        }
        empty={
          <>
            No seen shows yet. Mark shows as seen from the{' '}
            <Link to="/feed" className="text-secondary-red underline">
              feed
            </Link>{' '}
            or a show page.
          </>
        }
      />
      {editingShow && (
        <SeenNoteModal
          showId={editingShow.show_id}
          initialNote={editingShow.notes ?? ''}
          onClose={() => setEditingShow(null)}
          onSaved={notes =>
            setShows(prev =>
              prev.map(s => (s.show_id === editingShow.show_id ? { ...s, notes } : s))
            )
          }
        />
      )}
    </>
  );
}

export default requireAuth(SeenShowsPage);
